import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  MANCHESTER_PDF, registerAnalysisFont, drawMasthead, drawLogoRow, drawFooterLine, drawAnalysisSectionTitle,
  analysisTableStyles, loadLogoImage, PREPARED_BY,
} from './pdfBranding';

// ============================================================================
// Company Brands
// ============================================================================

export interface CompanyBrand {
  key: string;
  displayName: string;
  primary: [number, number, number];
  accent: [number, number, number];
  description: string;
  stylePrompt: string;
}

export const COMPANY_BRANDS: CompanyBrand[] = [
  {
    key: 'apex',
    displayName: 'Apex Real Estate',
    primary: [120, 0, 20],
    accent: [178, 31, 36],
    description: 'Deep maroon with red accents — Apex branding',
    stylePrompt: '',
  },
  {
    key: 'kw_commercial',
    displayName: 'KW Commercial',
    primary: [180, 0, 0],
    accent: [51, 51, 51],
    description: 'Keller Williams Commercial red with dark accents',
    stylePrompt: '',
  },
  {
    key: 'keller_williams',
    displayName: 'Keller Williams',
    primary: [180, 0, 0],
    accent: [100, 100, 100],
    description: 'Classic KW red with gray accents',
    stylePrompt: '',
  },
  {
    key: 'coldwell_banker',
    displayName: 'Coldwell Banker',
    primary: [0, 50, 120],
    accent: [0, 100, 170],
    description: 'Navy blue with bright blue accents',
    stylePrompt: '',
  },
  {
    key: 'exp_realty',
    displayName: 'eXp Realty',
    primary: [0, 61, 121],
    accent: [28, 117, 188],
    description: 'Dark blue with sky blue accents',
    stylePrompt: '',
  },
  {
    key: 'exp_commercial',
    displayName: 'eXp Commercial',
    primary: [0, 47, 95],
    accent: [0, 90, 156],
    description: 'Deep navy with medium blue accents',
    stylePrompt: '',
  },
  {
    key: 'lee_associates',
    displayName: 'Lee & Associates',
    primary: [0, 51, 102],
    accent: [204, 153, 0],
    description: 'Navy blue with gold accents',
    stylePrompt: '',
  },
  {
    key: 'jll',
    displayName: 'JLL',
    primary: [0, 51, 102],
    accent: [218, 41, 28],
    description: 'Navy blue with red accents',
    stylePrompt: '',
  },
  {
    key: 'custom',
    displayName: 'Custom',
    primary: [50, 50, 50],
    accent: [100, 100, 100],
    description: 'Choose your own brand colors',
    stylePrompt: '',
  },
];

export function getCompanyBrand(key: string): CompanyBrand | undefined {
  return COMPANY_BRANDS.find(b => b.key === key);
}

// ============================================================================
// Color Themes — built from company brands
// ============================================================================

export type ThemeStyle =
  | 'apex'
  | 'kw_commercial'
  | 'keller_williams'
  | 'coldwell_banker'
  | 'exp_realty'
  | 'exp_commercial'
  | 'lee_associates'
  | 'jll'
  | 'custom'
  | 'modern'
  | 'corporate';

interface Theme {
  primary: [number, number, number];
  accent: [number, number, number];
  dark: [number, number, number];
  medium: [number, number, number];
  light: [number, number, number];
  white: [number, number, number];
}

function buildThemeFromBrand(brand: CompanyBrand): Theme {
  return {
    primary: brand.primary,
    accent: brand.accent,
    dark: [33, 33, 33],
    medium: [100, 100, 100],
    light: [220, 220, 220],
    white: [255, 255, 255],
  };
}

const THEMES: Record<string, Theme> = {};
for (const brand of COMPANY_BRANDS) {
  THEMES[brand.key] = buildThemeFromBrand(brand);
}
// Legacy themes kept for backward compatibility
THEMES['modern'] = {
  primary: [25, 25, 28],
  accent: [190, 30, 45],
  dark: [33, 33, 33],
  medium: [100, 100, 100],
  light: [220, 220, 220],
  white: [255, 255, 255],
};
THEMES['corporate'] = {
  primary: [0, 51, 102],
  accent: [0, 128, 155],
  dark: [33, 33, 33],
  medium: [100, 100, 100],
  light: [220, 220, 220],
  white: [255, 255, 255],
};

// ============================================================================
// Page Constants
// ============================================================================

// ⚠️ `let`, not `const`: the trade-area report prints on US LETTER in the Manchester chrome (his ruling
// 2026-09-09, LB10) while the older generators below still lay out on A4. Every helper reads these
// bindings at call time, so `usePage()` switches the geometry for one document and back again.
let PW = 210;
let PH = 297;
let MARGIN = 20;
let SIDEBAR_W = 4;
let LEFT = MARGIN + SIDEBAR_W + 2;
let CW = PW - LEFT - MARGIN;
const HEADER_H = 30;

export type BuilderPage = 'a4' | 'letter';
export function usePage(size: BuilderPage): void {
  if (size === 'letter') {
    PW = 215.9; PH = 279.4; MARGIN = 14; SIDEBAR_W = 0;
  } else {
    PW = 210; PH = 297; MARGIN = 20; SIDEBAR_W = 4;
  }
  LEFT = MARGIN + SIDEBAR_W + (size === 'letter' ? 0 : 2);
  CW = PW - LEFT - MARGIN;
}

/**
 * The face every helper sets. Helvetica for the older generators; the trade-area report registers
 * Carlito and points this at it for the length of one document (`finally` puts it back).
 */
let ACTIVE_FONT = 'helvetica';
/** How `pageBreak` opens an overflow page: the builder's sidebar and top stripe, or a bare page for the masthead. */
let PAGE_STYLE: 'builder' | 'manchester' = 'builder';
const MANCHESTER_TOP = 17;

// ============================================================================
// Types
// ============================================================================

interface PropertyData {
  address?: string;
  property_name?: string;
  city?: string;
  state?: string;
  zip?: string;
  property_type?: string;
  property_subtype?: string;
  building_size?: number;
  lot_size_acres?: number;
  year_built?: number;
  clear_height_ft?: number;
  dock_doors?: number;
  grade_doors?: number;
  percent_leased?: number;
  parking_spaces?: number;
  zoning?: string;
  owner_name?: string;
}

interface TransactionData {
  sale_price?: number;
  asking_price?: number;
  price_per_sf?: number;
  cap_rate?: number;
  noi?: number;
  transaction_type?: string;
  transaction_date?: string;
}

interface ContentSections {
  property_description?: string;
  executive_summary?: string;
  location_analysis?: string;
  property_highlights?: string;
  market_analysis?: string;
  team_intro?: string;
}

export interface GeneratorOptions {
  property: PropertyData;
  transaction?: TransactionData | null;
  content: ContentSections;
  companyName?: string;
  companyPhone?: string;
  companyEmail?: string;
  companyAddress?: string;
  style?: ThemeStyle;
  logoBase64?: string;
  customPrimary?: [number, number, number];
  customAccent?: [number, number, number];
  comps?: CompRow[]; // optional comparable-sales section (OM)
}

// ============================================================================
// Utility Helpers
// ============================================================================

function getTheme(options?: { style?: ThemeStyle; customPrimary?: [number, number, number]; customAccent?: [number, number, number] }): Theme {
  const style = options?.style || 'apex';
  const base = THEMES[style] || THEMES['apex'];

  // For 'custom' style, override primary/accent if provided
  if (style === 'custom') {
    return {
      ...base,
      primary: options?.customPrimary || base.primary,
      accent: options?.customAccent || base.accent,
    };
  }

  return base;
}

function fmt$(val?: number): string {
  if (!val) return 'N/A';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val);
}

function fmtNum(val?: number): string {
  if (!val) return 'N/A';
  return val.toLocaleString();
}

function buildAddress(p: PropertyData): string {
  return [p.address, p.city, p.state, p.zip].filter(Boolean).join(', ');
}

function buildMetrics(p: PropertyData, t?: TransactionData | null): { label: string; value: string }[] {
  const m: { label: string; value: string }[] = [];
  if (t?.asking_price) m.push({ label: 'ASKING PRICE', value: fmt$(t.asking_price) });
  if (t?.sale_price) m.push({ label: 'SALE PRICE', value: fmt$(t.sale_price) });
  if (p.building_size) m.push({ label: 'BUILDING SIZE', value: `${fmtNum(p.building_size)} SF` });
  if (t?.price_per_sf) m.push({ label: 'PRICE / SF', value: fmt$(t.price_per_sf) });
  if (t?.cap_rate) m.push({ label: 'CAP RATE', value: `${t.cap_rate}%` });
  if (t?.noi) m.push({ label: 'NOI', value: fmt$(t.noi) });
  if (p.year_built) m.push({ label: 'YEAR BUILT', value: String(p.year_built) });
  if (p.clear_height_ft) m.push({ label: 'CLEAR HEIGHT', value: `${p.clear_height_ft} FT` });
  if (p.percent_leased != null) m.push({ label: 'OCCUPANCY', value: `${p.percent_leased}%` });
  if (p.lot_size_acres) m.push({ label: 'LOT SIZE', value: `${p.lot_size_acres} AC` });
  return m;
}

function parseHighlights(raw?: string): string[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
    return [];
  } catch {
    return raw.split('\n').filter(l => l.trim().length > 0);
  }
}

function buildSpecs(p: PropertyData): [string, string][] {
  const specs: [string, string][] = [];
  if (p.building_size) specs.push(['Building Size', `${fmtNum(p.building_size)} SF`]);
  if (p.lot_size_acres) specs.push(['Lot Size', `${p.lot_size_acres} acres`]);
  if (p.year_built) specs.push(['Year Built', String(p.year_built)]);
  if (p.clear_height_ft) specs.push(['Clear Height', `${p.clear_height_ft} ft`]);
  if (p.dock_doors) specs.push(['Dock Doors', String(p.dock_doors)]);
  if (p.grade_doors) specs.push(['Grade-Level Doors', String(p.grade_doors)]);
  if (p.parking_spaces) specs.push(['Parking', `${p.parking_spaces} spaces`]);
  if (p.zoning) specs.push(['Zoning', p.zoning]);
  if (p.percent_leased != null) specs.push(['Occupancy', `${p.percent_leased}%`]);
  return specs;
}

/**
 * Safely embed a base64 logo image into the document.
 * Returns true if the logo was successfully added.
 */
function addLogo(doc: jsPDF, logoBase64: string, x: number, y: number, w: number, h: number): boolean {
  try {
    // Determine image type from data URI or default to PNG
    let format: 'PNG' | 'JPEG' = 'PNG';
    let data = logoBase64;
    if (logoBase64.startsWith('data:image/jpeg') || logoBase64.startsWith('data:image/jpg')) {
      format = 'JPEG';
    }
    // Strip data URI prefix if present
    if (data.includes(',')) {
      data = data.split(',')[1];
    }
    doc.addImage(data, format, x, y, w, h);
    return true;
  } catch {
    return false;
  }
}

// ============================================================================
// Drawing Primitives
// ============================================================================

/**
 * Draw cover page — full-color background with centered white content card
 */
function drawCover(
  doc: jsPDF,
  title: string,
  options: GeneratorOptions,
  theme: Theme
): void {
  const { property, transaction, companyName, logoBase64 } = options;
  const company = companyName || 'Apex Real Estate Services';

  // Full page primary fill
  doc.setFillColor(...theme.primary);
  doc.rect(0, 0, PW, PH, 'F');

  // Accent stripe at very top
  doc.setFillColor(...theme.accent);
  doc.rect(0, 0, PW, 6, 'F');

  // White content card
  const cardX = 28;
  const cardY = 50;
  const cardW = PW - 56;
  const cardH = 170;
  doc.setFillColor(...theme.white);
  doc.roundedRect(cardX, cardY, cardW, cardH, 3, 3, 'F');

  // Logo above company name label (inside card)
  let y = cardY + 12;
  if (logoBase64) {
    const logoW = 30;
    const logoH = 30;
    const logoX = PW / 2 - logoW / 2;
    const added = addLogo(doc, logoBase64, logoX, y - 6, logoW, logoH);
    if (added) {
      y += logoH - 2;
    }
  }

  // Document type label
  y += 8;
  doc.setFont(ACTIVE_FONT, 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...theme.accent);
  doc.text(title, PW / 2, y, { align: 'center' });

  // Thin accent line below label
  y += 6;
  doc.setDrawColor(...theme.accent);
  doc.setLineWidth(0.8);
  const labelW = doc.getTextWidth(title);
  doc.line(PW / 2 - labelW / 2 - 8, y, PW / 2 + labelW / 2 + 8, y);

  // Property name
  y += 18;
  doc.setFont(ACTIVE_FONT, 'bold');
  doc.setFontSize(24);
  doc.setTextColor(...theme.dark);
  const propName = property.property_name || property.address || 'Property';
  const nameLines = doc.splitTextToSize(propName, cardW - 30);
  for (const line of nameLines) {
    doc.text(line, PW / 2, y, { align: 'center' });
    y += 11;
  }

  // Address
  y += 2;
  doc.setFont(ACTIVE_FONT, 'normal');
  doc.setFontSize(11);
  doc.setTextColor(...theme.medium);
  doc.text(buildAddress(property), PW / 2, y, { align: 'center' });
  y += 12;

  // Property type badge
  const typeLabel = (property.property_subtype || property.property_type || 'commercial').replace(/_/g, ' ').toUpperCase();
  doc.setFont(ACTIVE_FONT, 'bold');
  doc.setFontSize(8);
  const badgeW = doc.getTextWidth(typeLabel) + 16;
  doc.setFillColor(245, 245, 245);
  doc.roundedRect(PW / 2 - badgeW / 2, y - 4, badgeW, 10, 2, 2, 'F');
  doc.setTextColor(...theme.accent);
  doc.text(typeLabel, PW / 2, y + 2, { align: 'center' });

  // Transaction type badge (FOR SALE / FOR LEASE)
  if (transaction?.transaction_type) {
    const txType = transaction.transaction_type.toUpperCase().includes('LEASE') ? 'FOR LEASE' : 'FOR SALE';
    y += 14;
    doc.setFont(ACTIVE_FONT, 'bold');
    doc.setFontSize(9);
    const txBadgeW = doc.getTextWidth(txType) + 20;
    doc.setFillColor(...theme.accent);
    doc.roundedRect(PW / 2 - txBadgeW / 2, y - 5, txBadgeW, 12, 2, 2, 'F');
    doc.setTextColor(...theme.white);
    doc.text(txType, PW / 2, y + 2, { align: 'center' });
    y += 14;
  } else {
    y += 8;
  }

  // Divider
  y += 4;
  doc.setDrawColor(...theme.light);
  doc.setLineWidth(0.3);
  doc.line(cardX + 20, y, cardX + cardW - 20, y);
  y += 12;

  // Key metrics on cover (up to 4)
  const metrics = buildMetrics(property, transaction).slice(0, 4);
  if (metrics.length > 0) {
    const mColW = (cardW - 40) / metrics.length;
    metrics.forEach((m, i) => {
      const mx = cardX + 20 + i * mColW + mColW / 2;
      doc.setFont(ACTIVE_FONT, 'bold');
      doc.setFontSize(14);
      doc.setTextColor(...theme.dark);
      doc.text(m.value, mx, y, { align: 'center' });
      doc.setFont(ACTIVE_FONT, 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...theme.medium);
      doc.text(m.label, mx, y + 6, { align: 'center' });
    });
  }

  // Company name at bottom of page
  doc.setFont(ACTIVE_FONT, 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...theme.white);
  doc.text(company.toUpperCase(), PW / 2, PH - 35, { align: 'center' });

  if (title === 'OFFERING MEMORANDUM') {
    doc.setFont(ACTIVE_FONT, 'normal');
    doc.setFontSize(7);
    doc.setTextColor(180, 180, 180);
    doc.text('CONFIDENTIAL', PW / 2, PH - 26, { align: 'center' });
  }
}

/**
 * Start a new interior page with colored header bar + sidebar accent
 * Returns the Y position where content should start
 */
function startInteriorPage(doc: jsPDF, title: string, theme: Theme, companyName: string, logoBase64?: string, logoSize = 10): number {
  doc.addPage();

  // Header bar
  doc.setFillColor(...theme.primary);
  doc.rect(0, 0, PW, HEADER_H, 'F');

  // Accent stripe below header
  doc.setFillColor(...theme.accent);
  doc.rect(0, HEADER_H, PW, 2, 'F');

  // Logo right-aligned in header (logoSize configurable for a more prominent brand)
  if (logoBase64) {
    addLogo(doc, logoBase64, PW - MARGIN - logoSize, 3, logoSize, logoSize);
  }

  // Company name in header (small, top-left)
  doc.setFont(ACTIVE_FONT, 'normal');
  doc.setFontSize(7);
  doc.setTextColor(...theme.white);
  doc.text(companyName.toUpperCase(), MARGIN, 10);

  // Page title in header
  doc.setFont(ACTIVE_FONT, 'bold');
  doc.setFontSize(16);
  doc.setTextColor(...theme.white);
  doc.text(title, MARGIN, HEADER_H - 8);

  // Left sidebar accent stripe
  doc.setFillColor(...theme.accent);
  doc.rect(0, HEADER_H + 2, SIDEBAR_W, PH - HEADER_H - 2, 'F');

  return HEADER_H + 14;
}

/**
 * Draw a colored section bar (full-width accent background with white text)
 */
function drawSectionBar(doc: jsPDF, title: string, y: number, theme: Theme): number {
  y = pageBreak(doc, y, 22, theme);

  doc.setFillColor(...theme.accent);
  doc.rect(LEFT - 2, y - 4, CW + 4, 10, 'F');

  doc.setFont(ACTIVE_FONT, 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...theme.white);
  doc.text(title.toUpperCase(), LEFT + 4, y + 3);

  return y + 14;
}

/**
 * Draw a section title (text with accent-colored underline)
 */
function drawSectionTitle(doc: jsPDF, title: string, y: number, theme: Theme): number {
  y = pageBreak(doc, y, 16, theme);

  doc.setFont(ACTIVE_FONT, 'bold');
  doc.setFontSize(12);
  doc.setTextColor(...theme.accent);
  doc.text(title, LEFT, y);

  y += 2;
  doc.setDrawColor(...theme.accent);
  doc.setLineWidth(0.6);
  doc.line(LEFT, y, LEFT + doc.getTextWidth(title) * 1.05, y);

  return y + 8;
}

/**
 * Draw paragraph text
 */
function drawParagraph(doc: jsPDF, text: string, y: number, theme: Theme, fontSize: number = 9.5): number {
  doc.setFont(ACTIVE_FONT, 'normal');
  doc.setFontSize(fontSize);
  doc.setTextColor(...theme.dark);

  const lines = doc.splitTextToSize(text, CW);
  const lineH = fontSize * 0.42;

  for (const line of lines) {
    y = pageBreak(doc, y, lineH + 1, theme);
    doc.text(line, LEFT, y);
    y += lineH;
  }

  return y + 4;
}

/**
 * Draw metrics in card-style grid with left accent border
 */
function drawMetricsCards(doc: jsPDF, metrics: { label: string; value: string }[], y: number, theme: Theme): number {
  const display = metrics.slice(0, 8);
  if (display.length === 0) return y;

  const cols = Math.min(display.length, 4);
  const rows = Math.ceil(display.length / cols);
  const cardW = (CW - (cols - 1) * 4) / cols;
  const cardH = 22;

  y = pageBreak(doc, y, rows * (cardH + 4) + 4, theme);

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const idx = r * cols + c;
      if (idx >= display.length) break;

      const m = display[idx];
      const cx = LEFT + c * (cardW + 4);
      const cy = y + r * (cardH + 4);

      // Card background
      doc.setFillColor(248, 248, 248);
      doc.roundedRect(cx, cy, cardW, cardH, 1.5, 1.5, 'F');

      // Left accent border
      doc.setFillColor(...theme.accent);
      doc.rect(cx, cy + 2, 2, cardH - 4, 'F');

      // Value
      doc.setFont(ACTIVE_FONT, 'bold');
      doc.setFontSize(11);
      doc.setTextColor(...theme.dark);
      doc.text(m.value, cx + 8, cy + 9);

      // Label
      doc.setFont(ACTIVE_FONT, 'normal');
      doc.setFontSize(6.5);
      doc.setTextColor(...theme.medium);
      doc.text(m.label, cx + 8, cy + 16);
    }
  }

  return y + rows * (cardH + 4) + 6;
}

/**
 * Draw bullet list with accent-colored dots
 */
function drawBulletList(doc: jsPDF, items: string[], y: number, theme: Theme): number {
  for (const item of items) {
    y = pageBreak(doc, y, 8, theme);

    doc.setFillColor(...theme.accent);
    doc.circle(LEFT + 2, y - 1.2, 1.2, 'F');

    doc.setFont(ACTIVE_FONT, 'normal');
    doc.setFontSize(9.5);
    doc.setTextColor(...theme.dark);
    const lines = doc.splitTextToSize(item, CW - 10);
    for (const line of lines) {
      y = pageBreak(doc, y, 5, theme);
      doc.text(line, LEFT + 8, y);
      y += 4.2;
    }
    y += 1.5;
  }
  return y + 3;
}

/**
 * Draw specs table with colored header and alternating rows
 */
function drawSpecsTable(doc: jsPDF, specs: [string, string][], y: number, theme: Theme): number {
  y = pageBreak(doc, y, 14 + specs.length * 8, theme);

  autoTable(doc, {
    startY: y,
    head: [['Specification', 'Detail']],
    body: specs,
    theme: 'striped',
    styles: {
      fontSize: 9,
      cellPadding: 4,
      textColor: theme.dark,
      lineWidth: 0.1,
      lineColor: theme.light,
    },
    headStyles: {
      fillColor: theme.primary,
      textColor: theme.white,
      fontStyle: 'bold',
      fontSize: 8.5,
    },
    alternateRowStyles: {
      fillColor: [248, 248, 248],
    },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 55 },
    },
    margin: { left: LEFT, right: MARGIN },
  });

  return (doc as any).lastAutoTable.finalY + 8;
}

/**
 * Draw financial data table
 */
function drawFinancialTable(doc: jsPDF, data: [string, string][], y: number, theme: Theme): number {
  y = pageBreak(doc, y, 14 + data.length * 10, theme);

  autoTable(doc, {
    startY: y,
    head: [['Financial Metric', 'Value']],
    body: data,
    theme: 'striped',
    styles: {
      fontSize: 10,
      cellPadding: 5,
      textColor: theme.dark,
      lineWidth: 0.1,
      lineColor: theme.light,
    },
    headStyles: {
      fillColor: theme.primary,
      textColor: theme.white,
      fontStyle: 'bold',
    },
    alternateRowStyles: {
      fillColor: [248, 248, 248],
    },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 80 },
    },
    margin: { left: LEFT, right: MARGIN },
  });

  return (doc as any).lastAutoTable.finalY + 8;
}

/**
 * Draw contact / back page with full-color background
 */
function drawContactPage(doc: jsPDF, options: GeneratorOptions, theme: Theme): void {
  doc.addPage();

  // Full page fill
  doc.setFillColor(...theme.primary);
  doc.rect(0, 0, PW, PH, 'F');

  // Accent bar at top
  doc.setFillColor(...theme.accent);
  doc.rect(0, 0, PW, 6, 'F');

  // White content card centered
  const cardX = 35;
  const cardY = 80;
  const cardW = PW - 70;
  const cardH = 130;
  doc.setFillColor(...theme.white);
  doc.roundedRect(cardX, cardY, cardW, cardH, 3, 3, 'F');

  let y = cardY + 16;

  // Logo above company name
  if (options.logoBase64) {
    const logoW = 24;
    const logoH = 24;
    const added = addLogo(doc, options.logoBase64, PW / 2 - logoW / 2, y - 4, logoW, logoH);
    if (added) {
      y += logoH + 4;
    }
  }

  // "FOR MORE INFORMATION"
  doc.setFont(ACTIVE_FONT, 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...theme.medium);
  doc.text('FOR MORE INFORMATION', PW / 2, y, { align: 'center' });
  y += 12;

  // Accent divider line
  doc.setDrawColor(...theme.accent);
  doc.setLineWidth(1);
  doc.line(PW / 2 - 22, y, PW / 2 + 22, y);
  y += 16;

  // Company name
  const company = options.companyName || 'Apex Real Estate Services';
  doc.setFont(ACTIVE_FONT, 'bold');
  doc.setFontSize(16);
  doc.setTextColor(...theme.dark);
  doc.text(company, PW / 2, y, { align: 'center' });
  y += 14;

  // Contact details
  doc.setFont(ACTIVE_FONT, 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...theme.medium);

  if (options.companyPhone) {
    doc.text(options.companyPhone, PW / 2, y, { align: 'center' });
    y += 7;
  }
  if (options.companyEmail) {
    doc.text(options.companyEmail, PW / 2, y, { align: 'center' });
    y += 7;
  }
  if (options.companyAddress) {
    doc.text(options.companyAddress, PW / 2, y, { align: 'center' });
    y += 7;
  }

  // Disclaimer at bottom
  doc.setFont(ACTIVE_FONT, 'normal');
  doc.setFontSize(6.5);
  doc.setTextColor(180, 180, 180);
  const disclaimer = 'Information deemed reliable but not guaranteed. Buyer to verify all information independently.';
  doc.text(disclaimer, PW / 2, PH - 20, { align: 'center' });
}

/**
 * Add footers to all interior pages (skip cover and contact/back page)
 */
function drawAllFooters(doc: jsPDF, companyName: string, theme: Theme): void {
  const pages = doc.getNumberOfPages();
  for (let i = 2; i <= pages; i++) {
    // Skip the last page (contact/back page)
    if (i === pages) continue;

    doc.setPage(i);

    // Footer line
    doc.setDrawColor(...theme.light);
    doc.setLineWidth(0.3);
    doc.line(LEFT, PH - 14, PW - MARGIN, PH - 14);

    // Left: company name
    doc.setFont(ACTIVE_FONT, 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...theme.medium);
    doc.text(companyName, LEFT, PH - 9);

    // Right: page number
    doc.text(`${i - 1}`, PW - MARGIN, PH - 9, { align: 'right' });
  }
}

/**
 * Page break check — creates new overflow page with sidebar + top accent
 */
function pageBreak(doc: jsPDF, y: number, needed: number, theme: Theme): number {
  if (PAGE_STYLE === 'manchester') {
    // The masthead and the footer line are drawn on every page at the end; a new page is bare.
    if (y + needed > PH - 22) { doc.addPage(); return MANCHESTER_TOP; }
    return y;
  }
  if (y + needed > PH - 18) {
    doc.addPage();

    // Sidebar accent
    doc.setFillColor(...theme.accent);
    doc.rect(0, 0, SIDEBAR_W, PH, 'F');

    // Top accent bar for visual continuity
    doc.setFillColor(...theme.primary);
    doc.rect(0, 0, PW, 3, 'F');

    return 14;
  }
  return y;
}

// ============================================================================
// BROCHURE TEMPLATE (4-6 pages)
// ============================================================================

export function generateBrochurePDF(options: GeneratorOptions): jsPDF {
  const { property, transaction, content, companyName, logoBase64 } = options;
  const doc = new jsPDF('p', 'mm', 'a4');
  const theme = getTheme(options);
  const company = companyName || 'Apex Real Estate Services';

  // === PAGE 1: COVER ===
  drawCover(doc, 'PROPERTY BROCHURE', options, theme);

  // === PAGE 2: EXECUTIVE SUMMARY ===
  let y = startInteriorPage(doc, 'EXECUTIVE SUMMARY', theme, company, logoBase64);

  // Property address block
  doc.setFont(ACTIVE_FONT, 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...theme.dark);
  doc.text(property.property_name || property.address || 'Property', LEFT, y);
  y += 5;
  doc.setFont(ACTIVE_FONT, 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...theme.medium);
  doc.text(buildAddress(property), LEFT, y);
  y += 10;

  // Key metrics cards
  const metrics = buildMetrics(property, transaction);
  if (metrics.length > 0) {
    y = drawMetricsCards(doc, metrics.slice(0, 4), y, theme);
  }

  // Executive summary text (if available), otherwise property description
  if (content.executive_summary) {
    y = drawSectionTitle(doc, 'Overview', y, theme);
    y = drawParagraph(doc, content.executive_summary, y, theme);
  } else if (content.property_description) {
    y = drawSectionTitle(doc, 'Property Overview', y, theme);
    y = drawParagraph(doc, content.property_description, y, theme);
  }

  // === PAGE 3: PROPERTY DETAILS ===
  y = startInteriorPage(doc, 'PROPERTY DETAILS', theme, company, logoBase64);

  // Highlights
  const highlights = parseHighlights(content.property_highlights);
  if (highlights.length > 0) {
    y = drawSectionTitle(doc, 'Property Highlights', y, theme);
    y = drawBulletList(doc, highlights, y, theme);
  }

  // Building specs
  const specs = buildSpecs(property);
  if (specs.length > 0) {
    y = drawSectionBar(doc, 'Building Specifications', y, theme);
    y = drawSpecsTable(doc, specs, y, theme);
  }

  // Property description if we used exec summary on previous page
  if (content.executive_summary && content.property_description) {
    y = drawSectionTitle(doc, 'Property Description', y, theme);
    y = drawParagraph(doc, content.property_description, y, theme);
  }

  // === PAGE 4: LOCATION ANALYSIS (if available) ===
  if (content.location_analysis) {
    y = startInteriorPage(doc, 'LOCATION OVERVIEW', theme, company, logoBase64);
    y = drawParagraph(doc, content.location_analysis, y, theme);
  }

  // === LAST PAGE: CONTACT ===
  drawContactPage(doc, options, theme);

  // Footers
  drawAllFooters(doc, company, theme);

  return doc;
}

// ============================================================================
// OFFERING MEMORANDUM TEMPLATE (6-10 pages)
// ============================================================================

export function generateOMPDF(options: GeneratorOptions): jsPDF {
  const { property, transaction, content, companyName, logoBase64 } = options;
  const doc = new jsPDF('p', 'mm', 'a4');
  const theme = getTheme(options);
  const company = companyName || 'Apex Real Estate Services';

  // === PAGE 1: COVER ===
  drawCover(doc, 'OFFERING MEMORANDUM', options, theme);

  // === PAGE 2: TABLE OF CONTENTS ===
  let y = startInteriorPage(doc, 'TABLE OF CONTENTS', theme, company, logoBase64);
  y += 8;

  const tocItems: string[] = ['Executive Summary', 'Property Overview'];
  if (parseHighlights(content.property_highlights).length > 0) tocItems.push('Property Highlights');
  tocItems.push('Building Specifications');
  if (transaction && (transaction.sale_price || transaction.asking_price || transaction.noi)) {
    tocItems.push('Financial Analysis');
  }
  if (content.location_analysis) tocItems.push('Location Analysis');
  tocItems.push('Confidentiality Notice');

  tocItems.forEach((item, i) => {
    // Numbered label in accent color
    doc.setFont(ACTIVE_FONT, 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...theme.accent);
    doc.text(`${String(i + 1).padStart(2, '0')}`, LEFT, y);

    // Item name
    doc.setFont(ACTIVE_FONT, 'normal');
    doc.setFontSize(11);
    doc.setTextColor(...theme.dark);
    doc.text(item, LEFT + 14, y);

    // Dotted line to right
    doc.setDrawColor(...theme.light);
    doc.setLineWidth(0.3);
    const textEnd = LEFT + 14 + doc.getTextWidth(item) + 4;
    doc.line(textEnd, y - 1, PW - MARGIN - 20, y - 1);

    y += 12;
  });

  // === PAGE 3: EXECUTIVE SUMMARY ===
  y = startInteriorPage(doc, 'EXECUTIVE SUMMARY', theme, company, logoBase64);

  if (content.executive_summary) {
    y = drawParagraph(doc, content.executive_summary, y, theme, 10);
  }

  // Key metrics
  const metrics = buildMetrics(property, transaction);
  if (metrics.length > 0) {
    y += 4;
    y = drawMetricsCards(doc, metrics.slice(0, 8), y, theme);
  }

  // === PAGE 4: PROPERTY OVERVIEW ===
  y = startInteriorPage(doc, 'PROPERTY OVERVIEW', theme, company, logoBase64);

  if (content.property_description) {
    y = drawParagraph(doc, content.property_description, y, theme);
    y += 4;
  }

  // Highlights
  const highlights = parseHighlights(content.property_highlights);
  if (highlights.length > 0) {
    y = drawSectionTitle(doc, 'Property Highlights', y, theme);
    y = drawBulletList(doc, highlights, y, theme);
  }

  // === PAGE 5: BUILDING SPECS ===
  y = startInteriorPage(doc, 'BUILDING SPECIFICATIONS', theme, company, logoBase64);

  const specs = buildSpecs(property);
  if (specs.length > 0) {
    y = drawSpecsTable(doc, specs, y, theme);
  }

  // === PAGE 6: FINANCIAL ANALYSIS (if data available) ===
  if (transaction && (transaction.sale_price || transaction.asking_price || transaction.noi)) {
    y = startInteriorPage(doc, 'FINANCIAL ANALYSIS', theme, company, logoBase64);

    const finData: [string, string][] = [];
    if (transaction.asking_price) finData.push(['Asking Price', fmt$(transaction.asking_price)]);
    if (transaction.sale_price) finData.push(['Sale Price', fmt$(transaction.sale_price)]);
    if (transaction.price_per_sf) finData.push(['Price Per SF', fmt$(transaction.price_per_sf)]);
    if (transaction.noi) finData.push(['Net Operating Income (NOI)', fmt$(transaction.noi)]);
    if (transaction.cap_rate) finData.push(['Capitalization Rate', `${transaction.cap_rate}%`]);

    if (finData.length > 0) {
      y = drawFinancialTable(doc, finData, y, theme);
    }
  }

  // === PAGE 7: LOCATION ANALYSIS ===
  if (content.location_analysis) {
    y = startInteriorPage(doc, 'LOCATION ANALYSIS', theme, company, logoBase64);
    y = drawParagraph(doc, content.location_analysis, y, theme);
  }

  // === COMPARABLE SALES (optional) ===
  if (options.comps && options.comps.length > 0) {
    drawCompSection(doc, options.comps, theme, company, logoBase64, 'Comparable Sales');
  }

  // === PAGE 8: CONFIDENTIALITY NOTICE ===
  y = startInteriorPage(doc, 'CONFIDENTIALITY NOTICE', theme, company, logoBase64);

  const confidText = `This Offering Memorandum has been prepared by ${company} for the exclusive use of authorized prospective purchasers. This document is confidential and proprietary and is not to be reproduced, distributed, or shared with any third party without the prior written consent of ${company}.

The information contained herein has been obtained from sources believed to be reliable but has not been independently verified. No representation, warranty, or guarantee of any kind is made regarding the accuracy or completeness of the information contained herein. Prospective purchasers are encouraged to conduct their own independent investigation and due diligence.

${company} and its affiliates, agents, and representatives make no representations or warranties, express or implied, regarding the condition of the property or the accuracy of the information contained in this Offering Memorandum. The owner reserves the right to withdraw the property from the market, change the terms of sale, or reject any offer without notice.

By accepting this Offering Memorandum, you agree to maintain the confidentiality of the information contained herein and to return it upon request.`;

  y = drawParagraph(doc, confidText, y, theme, 9);

  // === LAST PAGE: CONTACT ===
  drawContactPage(doc, options, theme);

  // Footers
  drawAllFooters(doc, company, theme);

  return doc;
}

// ============================================================================
// PROPOSAL TEMPLATE (4-6 pages)
// ============================================================================

export function generateProposalPDF(options: GeneratorOptions): jsPDF {
  const { property, transaction, content, companyName, companyAddress, logoBase64 } = options;
  const doc = new jsPDF('p', 'mm', 'a4');
  const theme = getTheme(options);
  const company = companyName || 'Apex Real Estate Services';

  // === PAGE 1: COVER ===
  // Proposal cover uses a left-bar accent design (different from brochure/OM)
  doc.setFillColor(...theme.white);
  doc.rect(0, 0, PW, PH, 'F');

  // Left accent bars
  doc.setFillColor(...theme.primary);
  doc.rect(0, 0, 10, PH, 'F');
  doc.setFillColor(...theme.accent);
  doc.rect(10, 0, 3, PH, 'F');

  let y = 60;

  // "INVESTMENT PROPOSAL"
  doc.setFont(ACTIVE_FONT, 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...theme.accent);
  doc.text('INVESTMENT PROPOSAL', MARGIN + 12, y);
  y += 15;

  // Property name
  doc.setFont(ACTIVE_FONT, 'bold');
  doc.setFontSize(26);
  doc.setTextColor(...theme.dark);
  const propTitle = property.property_name || property.address || 'Property';
  const titleLines = doc.splitTextToSize(propTitle, PW - MARGIN - 24);
  for (const line of titleLines) {
    doc.text(line, MARGIN + 12, y);
    y += 13;
  }

  // Address
  y += 2;
  doc.setFont(ACTIVE_FONT, 'normal');
  doc.setFontSize(11);
  doc.setTextColor(...theme.medium);
  doc.text(buildAddress(property), MARGIN + 12, y);
  y += 16;

  // Type badge
  const typeLabel = (property.property_subtype || property.property_type || 'commercial').replace(/_/g, ' ').toUpperCase();
  doc.setFont(ACTIVE_FONT, 'bold');
  doc.setFontSize(8);
  const badgeW = doc.getTextWidth(typeLabel) + 16;
  doc.setFillColor(245, 245, 245);
  doc.roundedRect(MARGIN + 12, y - 4, badgeW, 10, 2, 2, 'F');
  doc.setTextColor(...theme.accent);
  doc.text(typeLabel, MARGIN + 20, y + 2);

  // Prepared by (bottom of cover)
  doc.setFont(ACTIVE_FONT, 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...theme.medium);
  doc.text('Prepared by', MARGIN + 12, PH - 65);
  doc.setFont(ACTIVE_FONT, 'bold');
  doc.setFontSize(12);
  doc.setTextColor(...theme.dark);
  doc.text(company, MARGIN + 12, PH - 55);
  if (companyAddress) {
    doc.setFont(ACTIVE_FONT, 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...theme.medium);
    doc.text(companyAddress, MARGIN + 12, PH - 47);
  }

  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  doc.setFont(ACTIVE_FONT, 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...theme.medium);
  doc.text(today, MARGIN + 12, PH - 35);

  // === PAGE 2: WHY [COMPANY] ===
  if (content.team_intro) {
    y = startInteriorPage(doc, `WHY ${company.toUpperCase()}`, theme, company, logoBase64);
    y = drawParagraph(doc, content.team_intro, y, theme, 10);
  }

  // === PAGE 3: MARKET ANALYSIS ===
  if (content.market_analysis) {
    y = startInteriorPage(doc, 'MARKET ANALYSIS', theme, company, logoBase64);
    y = drawParagraph(doc, content.market_analysis, y, theme, 10);
  }

  // === PAGE 4: PROPERTY RECOMMENDATION ===
  y = startInteriorPage(doc, 'PROPERTY RECOMMENDATION', theme, company, logoBase64);

  // Property header
  doc.setFont(ACTIVE_FONT, 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...theme.dark);
  doc.text(property.property_name || property.address || 'Property', LEFT, y);
  y += 5;
  doc.setFont(ACTIVE_FONT, 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...theme.medium);
  doc.text(buildAddress(property), LEFT, y);
  y += 10;

  // Metrics
  const metrics = buildMetrics(property, transaction);
  if (metrics.length > 0) {
    y = drawMetricsCards(doc, metrics.slice(0, 4), y, theme);
  }

  // Description
  if (content.property_description) {
    y = drawSectionTitle(doc, 'Property Overview', y, theme);
    y = drawParagraph(doc, content.property_description, y, theme);
  }

  // Highlights
  const highlights = parseHighlights(content.property_highlights);
  if (highlights.length > 0) {
    y = drawSectionTitle(doc, 'Key Highlights', y, theme);
    y = drawBulletList(doc, highlights, y, theme);
  }

  // === NEXT STEPS ===
  y = pageBreak(doc, y, 60, theme);
  y = drawSectionBar(doc, 'Next Steps', y, theme);

  const steps = [
    'Schedule a property tour at your convenience',
    'Review property due diligence materials',
    'Discuss financing options and deal structure',
    'Submit Letter of Intent',
  ];

  steps.forEach((step, i) => {
    y = pageBreak(doc, y, 12, theme);

    // Step number circle
    doc.setFillColor(...theme.accent);
    doc.circle(LEFT + 4, y - 1, 4, 'F');
    doc.setFont(ACTIVE_FONT, 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...theme.white);
    doc.text(String(i + 1), LEFT + 4, y + 0.5, { align: 'center' });

    // Step text
    doc.setFont(ACTIVE_FONT, 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...theme.dark);
    doc.text(step, LEFT + 14, y);
    y += 10;
  });

  // === LAST PAGE: CONTACT ===
  drawContactPage(doc, options, theme);

  // Footers
  drawAllFooters(doc, company, theme);

  return doc;
}

// ── Comparable Sales report ────────────────────────────────────────────────

export interface CompRow {
  address?: string;
  city?: string;
  state?: string;
  property_type?: string;
  building_size?: number | null;
  year_built?: number | null;
  sale_price?: number | null;
  price_per_sf?: number | null;
  cap_rate?: number | null;
  sale_date?: string | null;
}

export interface CompReportOptions {
  comps: CompRow[];
  companyName?: string;
  logoBase64?: string;
  style?: ThemeStyle;
  areaLabel?: string;
}

/** Standalone Apex-branded comparable-sales report (table + summary stats). */
/** Render a comparable-sales section (header + summary stats + table) onto `doc`. Shared by the standalone report and the OM. */
function drawCompSection(doc: jsPDF, rows: CompRow[], theme: Theme, company: string, logoBase64?: string, areaLabel?: string): void {
  const money = (v: number) => v >= 1_000_000 ? '$' + (v / 1_000_000).toFixed(2) + 'M' : v >= 1000 ? '$' + Math.round(v / 1000) + 'K' : '$' + v;
  const tcase = (s?: string) => (s ? s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '—');

  let y = startInteriorPage(doc, 'COMPARABLE SALES', theme, company, logoBase64);

  doc.setFont(ACTIVE_FONT, 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...theme.medium);
  doc.text(`${rows.length} comparable${rows.length === 1 ? '' : 's'}${areaLabel ? '  ·  ' + areaLabel : ''}`, LEFT, y);
  y += 8;

  const psfs = rows.map((r) => r.price_per_sf).filter((n): n is number => typeof n === 'number' && n > 0);
  const prices = rows.map((r) => r.sale_price).filter((n): n is number => typeof n === 'number' && n > 0);
  const avg = psfs.length ? Math.round(psfs.reduce((a, b) => a + b, 0) / psfs.length) : null;
  const sorted = [...psfs].sort((a, b) => a - b);
  const median = sorted.length ? sorted[Math.floor(sorted.length / 2)] : null;

  y = drawMetricsCards(doc, [
    { label: 'Comps', value: String(rows.length) },
    { label: 'Avg $/SF', value: avg != null ? '$' + avg : '—' },
    { label: 'Median $/SF', value: median != null ? '$' + median : '—' },
    { label: 'Price Range', value: prices.length ? `${money(Math.min(...prices))} - ${money(Math.max(...prices))}` : '—' },
  ], y, theme);
  y += 4;

  autoTable(doc, {
    startY: y,
    head: [['Address', 'City', 'Type', 'SF', 'Yr', 'Sale $', '$/SF', 'Cap', 'Date']],
    body: rows.map((r) => [
      r.address || '—',
      r.city || '—',
      tcase(r.property_type),
      r.building_size ? r.building_size.toLocaleString() : '—',
      r.year_built ? String(r.year_built) : '—',
      r.sale_price != null ? money(r.sale_price) : '—',
      r.price_per_sf != null ? '$' + r.price_per_sf : '—',
      r.cap_rate != null ? r.cap_rate + '%' : '—',
      r.sale_date || '—',
    ]),
    styles: { fontSize: 7.5, cellPadding: 1.5, textColor: theme.dark, lineWidth: 0.1, lineColor: theme.light },
    headStyles: { fillColor: theme.primary, textColor: theme.white, fontStyle: 'bold', fontSize: 7.5 },
    alternateRowStyles: { fillColor: [248, 248, 248] },
    margin: { left: LEFT, right: MARGIN },
    theme: 'grid',
  });
}

export function generateCompReportPDF(options: CompReportOptions): jsPDF {
  const doc = new jsPDF('p', 'mm', 'a4');
  const theme = getTheme(options);
  const company = options.companyName || 'Apex Real Estate Services';
  drawCompSection(doc, options.comps || [], theme, company, options.logoBase64, options.areaLabel);
  drawAllFooters(doc, company, theme);
  return doc;
}

// ============================================================================
// Trade Area Report — branded, infographic subject/rings report (+ optional Esri data)
// ============================================================================

export interface TradeAreaReportRing {
  miles: number;
  population: number;
  households: number;
  median_household_income: number | null;
  median_age: number | null;
  housing_units: number;
  owner_occupied: number;
  renter_occupied: number;
  bachelors_plus: number;
}

export interface TradeAreaReportSpendingArea {
  label: string;
  spending: { label: string; avgPerHousehold: number | null; spi: number | null }[];
  tapestrySegments: { name: string | null; pct: number | null }[];
  lifeMode: string | null;
  medianDisposableIncome: number | null;
  medianNetWorth: number | null;
  daytimePopulation?: number | null;
  daytimeWorkers?: number | null;
  perCapitaIncome?: number | null;
  retailDemand?: number | null;
  retailSupply?: number | null;
  retailGap?: number | null;
  retailLeakageFactor?: number | null;
}

export interface TradeAreaReportPermits {
  count: number;
  totalValuation: number;
  recent: { desc: string; date: string; valuation: number | null }[];
}

export interface TradeAreaReportComp {
  address: string;
  city?: string;
  sale_price?: number | null;
  price_per_sf?: number | null;
  sale_date?: string;
}

export interface TradeAreaReportOptions {
  subject: { label: string; lat: number; lng: number; isProperty?: boolean };
  rings: TradeAreaReportRing[];
  mapImageBase64?: string;
  spending?: { source?: string; areas: TradeAreaReportSpendingArea[] } | null;
  permits?: TradeAreaReportPermits | null;
  comps?: TradeAreaReportComp[] | null;
  companyName?: string;
  /** Kept for callers that still pass it; the Manchester chrome loads the marks itself. */
  style?: ThemeStyle;
}

// ── Native jsPDF infographic helpers (no charting lib) ──

// A brighter secondary palette so the report reads as more colorful than maroon-only.

/** Vertical bars comparing a metric across rings. */
function drawGroupedBars(doc: jsPDF, title: string, groups: { label: string; value: number }[], y: number, theme: Theme, fmt: (n: number) => string, barColor?: [number, number, number]): number {
  y = pageBreak(doc, y, 40, theme);
  doc.setFont(ACTIVE_FONT, 'bold'); doc.setFontSize(9); doc.setTextColor(...theme.dark);
  doc.text(title, LEFT, y); y += 3;
  const chartH = 22, base = y + chartH;
  const max = Math.max(1, ...groups.map((g) => g.value));
  const n = Math.max(1, groups.length), slot = CW / n, bw = Math.min(26, slot * 0.5);
  groups.forEach((g, i) => {
    const cx = LEFT + slot * i + slot / 2;
    const h = Math.max(0.5, (g.value / max) * chartH);
    doc.setFillColor(...(barColor || theme.primary)); doc.rect(cx - bw / 2, base - h, bw, h, 'F');
    doc.setFont(ACTIVE_FONT, 'bold'); doc.setFontSize(7); doc.setTextColor(...theme.dark);
    doc.text(fmt(g.value), cx, base - h - 1.5, { align: 'center' });
    doc.setFont(ACTIVE_FONT, 'normal'); doc.setFontSize(7); doc.setTextColor(...theme.medium);
    doc.text(g.label, cx, base + 4, { align: 'center' });
  });
  return base + 12; // the labels end at base + 6; the next title needs its own line
}

/** Horizontal SPI bars for one area, with a 100 = US-average marker. */
function drawSpiBars(doc: jsPDF, title: string, rows: { label: string; spi: number | null }[], y: number, theme: Theme): number {
  y = pageBreak(doc, y, 14 + rows.length * 6, theme);
  doc.setFont(ACTIVE_FONT, 'bold'); doc.setFontSize(9); doc.setTextColor(...theme.dark);
  doc.text(title, LEFT, y); y += 5;
  const labelW = 48, barX = LEFT + labelW, barW = CW - labelW - 14, scaleMax = 160;
  const baseX = barX + (100 / scaleMax) * barW;
  rows.forEach((r) => {
    const spi = r.spi ?? 0;
    doc.setFont(ACTIVE_FONT, 'normal'); doc.setFontSize(7.5); doc.setTextColor(...theme.medium);
    doc.text(r.label, LEFT, y + 3);
    doc.setFillColor(238, 238, 238); doc.rect(barX, y, barW, 3.4, 'F');
    const w = Math.min(1, spi / scaleMax) * barW;
    const col: [number, number, number] = spi >= 100 ? [16, 163, 74] : spi >= 80 ? [217, 119, 6] : [225, 29, 72];
    doc.setFillColor(...col); doc.rect(barX, y, w, 3.4, 'F');
    doc.setDrawColor(...theme.dark); doc.setLineWidth(0.3); doc.line(baseX, y - 0.6, baseX, y + 4);
    doc.setFont(ACTIVE_FONT, 'bold'); doc.setFontSize(7.5); doc.setTextColor(...theme.dark);
    doc.text(String(r.spi ?? '—'), barX + barW + 2, y + 3);
    y += 6;
  });
  doc.setFont(ACTIVE_FONT, 'italic'); doc.setFontSize(6.5); doc.setTextColor(...theme.medium);
  doc.text('SPI vs 100 = U.S. average (vertical line)', barX, y + 1);
  return y + 4;
}

/** Horizontal percent bars (Tapestry segments). */
function drawPctBars(doc: jsPDF, title: string, rows: { name: string; pct: number | null }[], footer: string, y: number, theme: Theme): number {
  y = pageBreak(doc, y, 12 + rows.length * 6, theme);
  doc.setFont(ACTIVE_FONT, 'bold'); doc.setFontSize(9); doc.setTextColor(...theme.dark);
  doc.text(title, LEFT, y); y += 5;
  const labelW = 58, barX = LEFT + labelW, barW = CW - labelW - 14;
  rows.forEach((r) => {
    const pct = r.pct ?? 0;
    doc.setFont(ACTIVE_FONT, 'normal'); doc.setFontSize(7.5); doc.setTextColor(...theme.dark);
    doc.text(r.name, LEFT, y + 3);
    doc.setFillColor(238, 238, 238); doc.rect(barX, y, barW, 3.4, 'F');
    doc.setFillColor(...theme.accent); doc.rect(barX, y, Math.min(1, pct / 100) * barW, 3.4, 'F');
    doc.setFont(ACTIVE_FONT, 'bold'); doc.setFontSize(7.5); doc.setTextColor(...theme.dark);
    doc.text(pct ? pct + '%' : '—', barX + barW + 2, y + 3);
    y += 6;
  });
  if (footer) { doc.setFont(ACTIVE_FONT, 'italic'); doc.setFontSize(7); doc.setTextColor(...theme.medium); doc.text(footer, LEFT, y + 1); y += 4; }
  return y;
}

const compactUSD = (n: number | null | undefined): string => {
  if (n == null) return '—';
  const s = n < 0 ? '-' : '';
  const a = Math.abs(n);
  if (a >= 1e9) return `${s}$${(a / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `${s}$${(a / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${s}$${Math.round(a / 1e3)}K`;
  return `${s}$${Math.round(a)}`;
};

/**
 * The trade-area report, in the MANCHESTER chrome - his ruling 2026-09-09 (LB10): "go ahead and do the
 * trade-area report too." US Letter; Carlito through every helper; the red masthead on every page with
 * the Apex and KW marks under it on page 1; TRADE AREA REPORT in red over a rule; navy section titles
 * over a red rule; navy-head zebra tables; the bar charts in navy and red; the footer line with both
 * brokers. The older generators in this file keep their own A4 look.
 */
export async function generateTradeAreaReportPDF(options: TradeAreaReportOptions): Promise<jsPDF> {
  usePage('letter');
  PAGE_STYLE = 'manchester';
  const doc = new jsPDF('p', 'mm', 'letter');
  const family = await registerAnalysisFont(doc);
  ACTIVE_FONT = family;
  try {
    const c = MANCHESTER_PDF;
    const theme: Theme = {
      primary: [...c.navy] as [number, number, number],
      accent: [...c.red] as [number, number, number],
      dark: [...c.ink] as [number, number, number],
      medium: [...c.muted] as [number, number, number],
      light: [...c.rule] as [number, number, number],
      white: [...c.white] as [number, number, number],
    };
    const frame = { pageWidth: PW, pageHeight: PH, margin: MARGIN };
    const [apexMark, kwMark] = await Promise.all([loadLogoImage('/apex-logo.png'), loadLogoImage('/kw-square.png')]);
    const company = options.companyName || 'Apex Real Estate Services';
    const rings = options.rings || [];
    const today = new Date();

    // A section names how tall its first block is: a table that would open at the foot of a page with
    // its head and two rows (the before, rendered 2026-09-09) starts on the next page instead.
    const section = (title: string, y: number, firstBlock = 12): number => {
      y = pageBreak(doc, y, 10 + firstBlock, theme);
      return drawAnalysisSectionTitle(doc, family, frame, title, y);
    };
    const ROW = 6.6; // a table row at these sizes, in mm
    const tableH = (rows: number) => (rows + 1) * ROW + 4;
    const mTable = (fs: number) => {
      const t = analysisTableStyles(family);
      return {
        styles: { ...t.styles, fontSize: fs, cellPadding: 2.2 },
        headStyles: { ...t.headStyles, fontSize: fs },
        alternateRowStyles: t.alternateRowStyles,
        columnStyles: { 0: { fontStyle: 'bold' as const } },
        margin: { left: LEFT, right: MARGIN, top: MANCHESTER_TOP, bottom: 22 },
        theme: 'plain' as const,
      };
    };

    const fmtN = (n: number | null | undefined) => (n == null ? '—' : Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 }));
    const fmtM = (n: number | null | undefined) => (n == null ? '—' : '$' + Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 }));
    const pctOf = (part: number, whole: number) => (whole > 0 ? Math.round((part / whole) * 100) + '%' : '—');

    const areas = options.spending?.areas;
    const primary = rings[rings.length - 1] || rings[0];
    const primaryArea = areas && areas.length ? areas[areas.length - 1] : null;

    // Title block, under the logo row
    let y = 32;
    doc.setTextColor(...c.red); doc.setFontSize(22); doc.setFont(family, 'bold');
    doc.text('TRADE AREA REPORT', LEFT, y); y += 4;
    doc.setDrawColor(...c.red); doc.setLineWidth(1.2); doc.line(LEFT, y, PW - MARGIN, y); y += 8;
    doc.setFont(family, 'bold'); doc.setFontSize(13); doc.setTextColor(...c.navy);
    doc.text(options.subject.label || 'Trade Area', LEFT, y); y += 5.5;
    doc.setFont(family, 'normal'); doc.setFontSize(8.5); doc.setTextColor(...c.muted);
    const kind = options.subject.isProperty ? 'Subject property' : 'Selected location';
    doc.text(`${kind}  ·  ${options.subject.lat.toFixed(5)}, ${options.subject.lng.toFixed(5)}`, LEFT, y); y += 6;

    // Static map image (best-effort)
    if (options.mapImageBase64) {
      const w = CW, h = Math.round(w * (420 / 640));
      y = pageBreak(doc, y, h + 6, theme);
      try {
        doc.addImage(options.mapImageBase64, 'PNG', LEFT, y, w, h);
        doc.setDrawColor(...c.rule); doc.setLineWidth(0.2); doc.rect(LEFT, y, w, h);
        y += h + 6;
      } catch { /* image is optional */ }
    }

    // KPI cards (primary ring)
    if (primary) {
      y = drawMetricsCards(doc, [
        { label: `Population (${primary.miles} mi)`, value: fmtN(primary.population) },
        { label: 'Median HH Income', value: fmtM(primary.median_household_income) },
        primaryArea?.daytimePopulation != null
          ? { label: 'Daytime Pop', value: fmtN(primaryArea.daytimePopulation) }
          : { label: 'Households', value: fmtN(primary.households) },
        { label: 'Median Age', value: primary.median_age == null ? '—' : String(primary.median_age) },
      ], y, theme) + 2;
    }

    // Demographics section: table + ring bar charts
    const demoRows = 8 + (areas && areas.length ? 3 : 0);
    y = section('TRADE-AREA DEMOGRAPHICS', y, tableH(demoRows));
    const demoBody: string[][] = [
      ['Population', ...rings.map((r) => fmtN(r.population))],
      ['Households', ...rings.map((r) => fmtN(r.households))],
      ['Median HH income', ...rings.map((r) => fmtM(r.median_household_income))],
      ['Median age', ...rings.map((r) => (r.median_age == null ? '—' : String(r.median_age)))],
      ['Housing units', ...rings.map((r) => fmtN(r.housing_units))],
      ['Owner-occupied', ...rings.map((r) => `${fmtN(r.owner_occupied)} (${pctOf(r.owner_occupied, r.owner_occupied + r.renter_occupied)})`)],
      ['Renter-occupied', ...rings.map((r) => `${fmtN(r.renter_occupied)} (${pctOf(r.renter_occupied, r.owner_occupied + r.renter_occupied)})`)],
      ["Bachelor's+", ...rings.map((r) => fmtN(r.bachelors_plus))],
    ];
    if (areas && areas.length) {
      demoBody.push(['Daytime pop', ...areas.map((a) => fmtN(a.daytimePopulation))]);
      demoBody.push(['Daytime workers', ...areas.map((a) => fmtN(a.daytimeWorkers))]);
      demoBody.push(['Per-capita income', ...areas.map((a) => fmtM(a.perCapitaIncome))]);
    }
    autoTable(doc, { startY: y, head: [['Metric', ...rings.map((r) => `${r.miles} mi`)]], body: demoBody, ...mTable(8.5) });
    y = (doc as any).lastAutoTable.finalY + 8;
    y = drawGroupedBars(doc, 'Population by radius', rings.map((r) => ({ label: `${r.miles} mi`, value: r.population })), y, theme, fmtN, theme.primary);
    y = drawGroupedBars(doc, 'Median household income by radius', rings.map((r) => ({ label: `${r.miles} mi`, value: r.median_household_income || 0 })), y, theme, fmtM, theme.accent);

    // Optional Esri spending & segments
    if (areas && areas.length) {
      const cats = areas[0].spending.map((sp) => sp.label);
      y = section('CONSUMER SPENDING & SEGMENTS (ESRI)', y, tableH(cats.length + 2));
      const spendRows: string[][] = cats.map((label, idx) => [
        label,
        ...areas.map((a) => {
          const sp = a.spending[idx];
          return !sp || sp.avgPerHousehold == null ? '—'
            : `$${Number(sp.avgPerHousehold).toLocaleString('en-US', { maximumFractionDigits: 0 })}${sp.spi != null ? ` (SPI ${sp.spi})` : ''}`;
        }),
      ]);
      spendRows.push(['Median disposable inc.', ...areas.map((a) => fmtM(a.medianDisposableIncome))]);
      spendRows.push(['Median net worth', ...areas.map((a) => fmtM(a.medianNetWorth))]);
      autoTable(doc, { startY: y, head: [['Avg $ / household / yr', ...areas.map((a) => a.label)]], body: spendRows, ...mTable(8) });
      y = (doc as any).lastAutoTable.finalY + 6;

      if (primaryArea) {
        y = drawSpiBars(doc, `Spending Potential Index · ${primaryArea.label}`, primaryArea.spending.map((sp) => ({ label: sp.label, spi: sp.spi })), y, theme);
      }

      // Retail Opportunity (demand vs supply) - on its own page for a cleaner layout
      if (areas.some((a) => a.retailGap != null)) {
        doc.addPage();
        y = MANCHESTER_TOP;
        y = section('RETAIL OPPORTUNITY (DEMAND vs SUPPLY)', y);
        autoTable(doc, {
          startY: y,
          head: [['Retail ($/yr)', ...areas.map((a) => a.label)]],
          body: [
            ['Consumer demand', ...areas.map((a) => compactUSD(a.retailDemand))],
            ['Retail supply (sales)', ...areas.map((a) => compactUSD(a.retailSupply))],
            ['Gap (demand − supply)', ...areas.map((a) => compactUSD(a.retailGap))],
            ['Leakage factor', ...areas.map((a) => (a.retailLeakageFactor != null ? `${a.retailLeakageFactor}%` : '—'))],
          ],
          ...mTable(8),
        });
        y = (doc as any).lastAutoTable.finalY + 4;
        const g = primaryArea?.retailGap;
        if (g != null && primaryArea) {
          const line = g > 0
            ? `≈${compactUSD(g)}/yr of retail spending leaks out of the ${primaryArea.label} area — an opportunity for new retail tenants to capture unmet local demand.`
            : `The ${primaryArea.label} area captures ≈${compactUSD(-g)}/yr more retail sales than residents spend — a retail destination pulling shoppers from beyond the ring.`;
          const wrapped = doc.splitTextToSize(line, CW) as string[];
          y = pageBreak(doc, y, wrapped.length * 4 + 3, theme);
          doc.setFont(family, 'italic'); doc.setFontSize(8); doc.setTextColor(...c.muted);
          doc.text(wrapped, LEFT, y); y += wrapped.length * 4 + 4;
        }
      }

      // Tapestry segments - bars for the primary ring, one-liners for the rest
      if (primaryArea) {
        const segs = primaryArea.tapestrySegments.filter((sg) => sg.name).map((sg) => ({ name: sg.name as string, pct: sg.pct }));
        if (segs.length) {
          y = drawPctBars(doc, `Top lifestyle segments · ${primaryArea.label}`, segs, primaryArea.lifeMode ? `Dominant LifeMode: ${primaryArea.lifeMode}` : '', y, theme);
        }
      }
      const others = areas.slice(0, Math.max(0, areas.length - 1));
      if (others.length) {
        doc.setFont(family, 'normal'); doc.setFontSize(7.5); doc.setTextColor(...c.muted);
        for (const a of others) {
          const segs = a.tapestrySegments.filter((sg) => sg.name).map((sg) => `${sg.name} ${sg.pct ?? ''}%`).join(', ');
          const line = `${a.label}: ${segs || '—'}${a.lifeMode ? '  ·  ' + a.lifeMode : ''}`;
          const wrapped = doc.splitTextToSize(line, CW) as string[];
          y = pageBreak(doc, y, wrapped.length * 4 + 1, theme);
          doc.text(wrapped, LEFT, y); y += wrapped.length * 4 + 1;
        }
        y += 3;
      }
    }

    // Development Activity (building permits in the area)
    if (options.permits && options.permits.count > 0) {
      y = section('DEVELOPMENT ACTIVITY', y, 30 + tableH(options.permits.recent.length));
      y = drawMetricsCards(doc, [
        { label: 'Permits in area', value: String(options.permits.count) },
        { label: 'Total valuation', value: compactUSD(options.permits.totalValuation) },
      ], y, theme) + 3;
      if (options.permits.recent.length) {
        autoTable(doc, {
          startY: y,
          head: [['Recent permit', 'Date', 'Valuation']],
          body: options.permits.recent.map((pm) => [pm.desc || '—', pm.date || '—', pm.valuation != null ? compactUSD(pm.valuation) : '—']),
          ...mTable(8),
        });
        y = (doc as any).lastAutoTable.finalY + 6;
      }
    }

    // Nearby recent sales
    if (options.comps && options.comps.length) {
      y = section('NEARBY RECENT SALES', y, tableH(options.comps.length));
      autoTable(doc, {
        startY: y,
        head: [['Address', 'City', 'Sale $', '$/SF', 'Date']],
        body: options.comps.map((cp) => [
          cp.address || '—', cp.city || '—',
          cp.sale_price != null ? compactUSD(cp.sale_price) : '—',
          cp.price_per_sf != null ? '$' + cp.price_per_sf : '—',
          cp.sale_date || '—',
        ]),
        ...mTable(8),
      });
      y = (doc as any).lastAutoTable.finalY + 6;
    }

    // The chrome: the masthead and the footer line on every page, the marks on page 1
    const footerLeft = `${company}  |  ${PREPARED_BY.map((b) => `${b.name} ${b.phone}`).join('  |  ')}  |  apex-res.com`;
    const dateLine = today.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const pageCount = doc.getNumberOfPages();
    for (let pg = 1; pg <= pageCount; pg++) {
      doc.setPage(pg);
      drawMasthead(doc, family, frame, company, dateLine);
      drawFooterLine(doc, family, frame, footerLeft, pg);
    }
    // The source line sits above the footer rule on the LAST page - the strip the content never
    // enters (its bottom margin is 22, the rule is at 16) - so it never takes a page of its own.
    doc.setPage(pageCount);
    doc.setFont(family, 'italic'); doc.setFontSize(7); doc.setTextColor(...c.muted);
    const src = options.spending?.source ? `U.S. Census ACS + ${options.spending.source}` : 'U.S. Census ACS (area-weighted estimates)';
    doc.text(`Source: ${src}. Generated ${today.toLocaleDateString('en-US')}.`, LEFT, PH - 18.5);
    doc.setPage(1);
    drawLogoRow(doc, family, frame, c.barHeight + 3, { apex: apexMark, kw: kwMark }, `${kind}  ·  Trade Area Report`);
    return doc;
  } finally {
    ACTIVE_FONT = 'helvetica';
    PAGE_STYLE = 'builder';
    usePage('a4');
  }
}
